import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import ListItemText from '@material-ui/core/ListItemText';
import Autocomplete, { highlightSubstring } from '../Autocomplete';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { index as searchApi } from '@afosto/content/api/Index';
import { useFormContext } from '../Form';
import translations from './translations';

const OrganisationSearch = ({
  disallowedOrganisations,
  contact,
  enableCreate,
  validateOrganisation,
  name,
  onChange,
  ...props
}) => {
  const intl = useIntl();
  const formContext = useFormContext();
  const { setValue } = formContext ?? {};
  const [organisationSuggestions, setOrganisationSuggestions] = useState([]);
  const [isFetchingOrganisations, setIsFetchingOrganisations] = useState(false);

  /**
   * Search for organisations
   * @param {string} searchValue
   * @returns {Promise}
   */
  const searchOrganisations = useCallback(
    async searchValue => {
      try {
        setIsFetchingOrganisations(true);

        const response = await searchApi.search({
          codes: ['rel'],
          models: ['organisations'],
          namespaces: ['default'],
          q: searchValue?.length > 0 ? [searchValue] : null,
        });
        const { data = {} } = response;
        const { hits = [] } = data;
        const suggestions = hits.reduce((acc, hit) => {
          const { metadata = {}, reference } = hit;

          if (!disallowedOrganisations.includes(reference)) {
            return [
              ...acc,
              {
                ...metadata,
                id: reference,
                label: metadata?.name ?? '',
                value: reference,
              },
            ];
          }

          return acc;
        }, []);
        setOrganisationSuggestions(suggestions);
      } catch (err) {
        setOrganisationSuggestions([]);
      } finally {
        setIsFetchingOrganisations(false);
      }
    },
    [disallowedOrganisations],
  );

  useEffect(() => {
    if (!contact) {
      searchOrganisations();
    }
  }, [contact, searchOrganisations]);

  useEffect(() => {
    if (contact) {
      const organisations = [
        ...(contact?.organisations?.primary ? [contact.organisations.primary] : []),
        ...(contact?.organisations?.secondary ? contact.organisations.secondary : []),
      ];
      const formattedOrganisations = organisations
        .filter(item => !item.deleted_at)
        .reduce(
          (acc, result) => [
            ...acc,
            {
              ...result,
              label: result.name,
              value: result.id,
            },
          ],
          [],
        );
      setOrganisationSuggestions(formattedOrganisations);
    } else {
      setOrganisationSuggestions([]);
    }
  }, [contact]);

  /**
   * Set the contact value on the form or trigger the provided onChange callback
   * @param {object|Event} ev
   * @param {object|null} organisation
   */
  const setOrganisationValue = (ev, organisation) => {
    if (formContext && !onChange) {
      setValue(name, organisation);
      return;
    }

    if (onChange) {
      onChange(ev, organisation);
    }
  };

  /**
   * Handle the change on the organisation search.
   * @param {object|Event} ev
   * @param {any} newValue
   */
  const handleOnChange = async (ev, newValue) => {
    try {
      const shouldValidateOrganisation = validateOrganisation && !enableCreate;

      if (!shouldValidateOrganisation) {
        setOrganisationValue(ev, newValue ?? null);
        return;
      }

      if (newValue) {
        const response = await organisationsApi.viewOrganisation(newValue?.id);
        const { data: organisation } = response;

        if (organisation) {
          setOrganisationValue(ev, {
            ...organisation,
            label: organisation.name,
            value: organisation.id,
          });
        } else {
          setOrganisationValue(ev, null);
        }
      } else {
        setOrganisationValue(ev, null);
      }
    } catch (err) {
      setOrganisationValue(ev, null);
    }
  };

  /**
   * Search organisations based on the input value.
   * @param {Event} event
   */
  const handleSearchOrganisation = event => {
    if (contact) {
      return;
    }

    const { value } = event?.target ?? {};
    searchOrganisations(value).catch(() => {
      // Do something with error.
    });
  };

  /**
   * Render the organisation options in the auto complete dropdown.
   * @param {object} option
   * @param {string} inputValue
   * @returns {Component}
   */
  const renderOrganisationOptions = (option, { inputValue = '' } = {}) => {
    let label = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      label = option.label;
    }

    return <ListItemText primary={highlightSubstring(label, inputValue)} />;
  };

  return (
    <Autocomplete
      {...props}
      name={name}
      onChange={handleOnChange}
      onInputChange={handleSearchOrganisation}
      label={intl.formatMessage(translations.fields.organisation.label)}
      enableCreate={enableCreate}
      options={organisationSuggestions}
      renderOption={renderOrganisationOptions}
      autocomplete="off"
      loading={isFetchingOrganisations}
    />
  );
};

OrganisationSearch.propTypes = {
  ...Autocomplete.propTypes,
  disallowedOrganisations: PropTypes.array,
  contact: PropTypes.object,
  validateOrganisation: PropTypes.bool,
};

OrganisationSearch.defaultProps = {
  ...Autocomplete.defaultProps,
  disallowedOrganisations: [],
  contact: undefined,
  validateOrganisation: true,
};

export default OrganisationSearch;
